import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { MessageSquare, Plus, Pencil, Trash2, Send, Eye, Users } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminMessaging() {
  const [templates, setTemplates] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [templateDialog, setTemplateDialog] = useState(false);
  const [campaignDialog, setCampaignDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [previewData, setPreviewData] = useState(null);

  const [templateForm, setTemplateForm] = useState({ name: '', channel: 'whatsapp', category: 'general', subject: '', body: '', variables: '' });
  const [campaignForm, setCampaignForm] = useState({ name: '', template_id: '', channel: 'whatsapp', target_criteria: {} });

  const fetchAll = async () => {
    try {
      const [t, c] = await Promise.all([api.get('/messaging/templates'), api.get('/messaging/campaigns')]);
      setTemplates(t.data);
      setCampaigns(c.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => { fetchAll(); }, []);

  const openTemplateDialog = (tmpl = null) => {
    setEditingTemplate(tmpl);
    setTemplateForm(tmpl ? { name: tmpl.name, channel: tmpl.channel, category: tmpl.category, subject: tmpl.subject, body: tmpl.body, variables: (tmpl.variables || []).join(', ') } : { name: '', channel: 'whatsapp', category: 'general', subject: '', body: '', variables: '' });
    setTemplateDialog(true);
  };

  const saveTemplate = async () => {
    const data = { ...templateForm, variables: templateForm.variables.split(',').map(v => v.trim()).filter(Boolean) };
    try {
      if (editingTemplate) await api.put(`/messaging/templates/${editingTemplate.template_id}`, data);
      else await api.post('/messaging/templates', data);
      toast.success(editingTemplate ? 'Template updated' : 'Template created');
      setTemplateDialog(false);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    await api.delete(`/messaging/templates/${id}`);
    toast.success('Template deleted');
    fetchAll();
  };

  const saveCampaign = async () => {
    try {
      await api.post('/messaging/campaigns', campaignForm);
      toast.success('Campaign created');
      setCampaignDialog(false);
      fetchAll();
    } catch (e) { toast.error('Failed'); }
  };

  const sendCampaign = async (id) => {
    if (!window.confirm('Send this campaign now?')) return;
    try {
      const res = await api.post(`/messaging/campaigns/${id}/send`);
      toast.success(res.data.message);
      fetchAll();
    } catch (e) { toast.error('Failed to send'); }
  };

  const previewCampaign = async (id) => {
    try {
      const res = await api.get(`/messaging/campaigns/${id}/preview`);
      setPreviewData(res.data);
    } catch { toast.error('Failed to load preview'); }
  };

  const categoryColors = { transactional: 'bg-blue-50 text-blue-700', reminder: 'bg-amber-50 text-amber-700', follow_up: 'bg-green-50 text-green-700', promotion: 'bg-purple-50 text-purple-700', general: 'bg-gray-100 text-gray-700' };
  const statusColors = { draft: 'bg-gray-100 text-gray-700', scheduled: 'bg-blue-50 text-blue-700', sent: 'bg-green-50 text-green-700' };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[#2D2D2D] border-t-transparent" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-messaging-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-[#1A1A1A]" style={{ fontFamily: 'Manrope' }}>Messaging</h1>
        <p className="text-sm text-[#5A5A5A] mt-1">Manage message templates and campaigns</p>
      </div>

      <Tabs defaultValue="templates" className="space-y-4">
        <TabsList className="bg-[#F5F5F0] rounded-full p-1 inline-flex">
          <TabsTrigger value="templates" className="rounded-full data-[state=active]:bg-[#2D2D2D] data-[state=active]:text-white text-sm px-5" data-testid="templates-tab">Templates ({templates.length})</TabsTrigger>
          <TabsTrigger value="campaigns" className="rounded-full data-[state=active]:bg-[#2D2D2D] data-[state=active]:text-white text-sm px-5" data-testid="campaigns-tab">Campaigns ({campaigns.length})</TabsTrigger>
        </TabsList>

        {/* Templates */}
        <TabsContent value="templates" className="space-y-4">
          <div className="flex justify-end">
            <Button className="rounded-full bg-[#2D2D2D] text-white" onClick={() => openTemplateDialog()} data-testid="add-template-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Template
            </Button>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {templates.map(t => (
              <Card key={t.template_id} className="rounded-2xl border-[#E5E5E0] bg-white card-hover" data-testid={`template-card-${t.template_id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#4A7C59]/10 rounded-xl flex items-center justify-center">
                        <MessageSquare className="w-5 h-5 text-[#4A7C59]" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#2D2D2D] text-sm">{t.name}</h3>
                        <p className="text-xs text-[#888] capitalize">{t.channel}</p>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => openTemplateDialog(t)} className="p-1.5 rounded-lg hover:bg-[#F5F5F0]"><Pencil className="w-3.5 h-3.5 text-[#888]" /></button>
                      <button onClick={() => deleteTemplate(t.template_id)} className="p-1.5 rounded-lg hover:bg-red-50"><Trash2 className="w-3.5 h-3.5 text-[#D9534F]" /></button>
                    </div>
                  </div>
                  <p className="text-sm text-[#5A5A5A] mb-2 line-clamp-2">{t.body}</p>
                  <div className="flex gap-2">
                    <span className={`text-xs px-2 py-0.5 rounded capitalize ${categoryColors[t.category] || categoryColors.general}`}>{t.category}</span>
                    {t.variables?.length > 0 && <span className="text-xs bg-[#F5F5F0] px-2 py-0.5 rounded">{t.variables.length} vars</span>}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Campaigns */}
        <TabsContent value="campaigns" className="space-y-4">
          <div className="flex justify-end">
            <Button className="rounded-full bg-[#2D2D2D] text-white" onClick={() => setCampaignDialog(true)} data-testid="add-campaign-btn">
              <Plus className="w-4 h-4 mr-2" /> New Campaign
            </Button>
          </div>
          {campaigns.length === 0 ? (
            <Card className="rounded-2xl border-[#E5E5E0] border-dashed bg-white"><CardContent className="p-8 text-center text-sm text-[#5A5A5A]">No campaigns yet. Create your first campaign.</CardContent></Card>
          ) : (
            <Card className="rounded-2xl border-[#E5E5E0] bg-white overflow-hidden">
              <Table>
                <TableHeader><TableRow className="bg-[#F9F9F7]">
                  <TableHead className="text-xs font-semibold text-[#5A5A5A]">Campaign</TableHead>
                  <TableHead className="text-xs font-semibold text-[#5A5A5A]">Template</TableHead>
                  <TableHead className="text-xs font-semibold text-[#5A5A5A]">Channel</TableHead>
                  <TableHead className="text-xs font-semibold text-[#5A5A5A]">Status</TableHead>
                  <TableHead className="text-xs font-semibold text-[#5A5A5A]">Sent</TableHead>
                  <TableHead className="text-xs font-semibold text-[#5A5A5A]">Actions</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {campaigns.map(c => (
                    <TableRow key={c.campaign_id} data-testid={`campaign-row-${c.campaign_id}`}>
                      <TableCell className="text-sm font-medium">{c.name}</TableCell>
                      <TableCell className="text-sm">{c.template_name}</TableCell>
                      <TableCell className="text-sm capitalize">{c.channel}</TableCell>
                      <TableCell><span className={`text-xs px-2 py-0.5 rounded ${statusColors[c.status] || ''}`}>{c.status}</span></TableCell>
                      <TableCell className="text-sm">{c.sent_count || 0}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <button onClick={() => previewCampaign(c.campaign_id)} className="p-1.5 rounded-lg hover:bg-[#F5F5F0]" title="Preview"><Eye className="w-3.5 h-3.5 text-[#888]" /></button>
                          {c.status === 'draft' && <button onClick={() => sendCampaign(c.campaign_id)} className="p-1.5 rounded-lg hover:bg-green-50" title="Send"><Send className="w-3.5 h-3.5 text-[#4A7C59]" /></button>}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Template Dialog */}
      <Dialog open={templateDialog} onOpenChange={setTemplateDialog}>
        <DialogContent className="rounded-2xl max-w-lg">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Manrope' }}>{editingTemplate ? 'Edit Template' : 'New Template'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-sm">Name *</Label><Input value={templateForm.name} onChange={e => setTemplateForm({ ...templateForm, name: e.target.value })} className="mt-1 rounded-lg" data-testid="template-name-input" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-sm">Channel</Label>
                <Select value={templateForm.channel} onValueChange={v => setTemplateForm({ ...templateForm, channel: v })}>
                  <SelectTrigger className="mt-1 rounded-lg"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="whatsapp">WhatsApp</SelectItem><SelectItem value="email">Email</SelectItem><SelectItem value="sms">SMS</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label className="text-sm">Category</Label>
                <Select value={templateForm.category} onValueChange={v => setTemplateForm({ ...templateForm, category: v })}>
                  <SelectTrigger className="mt-1 rounded-lg"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="transactional">Transactional</SelectItem><SelectItem value="reminder">Reminder</SelectItem><SelectItem value="follow_up">Follow Up</SelectItem><SelectItem value="promotion">Promotion</SelectItem><SelectItem value="general">General</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="text-sm">Subject</Label><Input value={templateForm.subject} onChange={e => setTemplateForm({ ...templateForm, subject: e.target.value })} className="mt-1 rounded-lg" /></div>
            <div><Label className="text-sm">Body *</Label><Textarea value={templateForm.body} onChange={e => setTemplateForm({ ...templateForm, body: e.target.value })} className="mt-1 rounded-lg" rows={3} placeholder="Use {{variable}} for dynamic content" data-testid="template-body-input" /></div>
            <div><Label className="text-sm">Variables (comma-separated)</Label><Input value={templateForm.variables} onChange={e => setTemplateForm({ ...templateForm, variables: e.target.value })} className="mt-1 rounded-lg" placeholder="name, pet_name, date" /></div>
          </div>
          <DialogFooter><Button className="rounded-full bg-[#2D2D2D] text-white" onClick={saveTemplate} data-testid="save-template-btn">{editingTemplate ? 'Update' : 'Create'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Campaign Dialog */}
      <Dialog open={campaignDialog} onOpenChange={setCampaignDialog}>
        <DialogContent className="rounded-2xl max-w-md">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Manrope' }}>New Campaign</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-sm">Campaign Name *</Label><Input value={campaignForm.name} onChange={e => setCampaignForm({ ...campaignForm, name: e.target.value })} className="mt-1 rounded-lg" data-testid="campaign-name-input" /></div>
            <div><Label className="text-sm">Template *</Label>
              <Select value={campaignForm.template_id} onValueChange={v => setCampaignForm({ ...campaignForm, template_id: v })}>
                <SelectTrigger className="mt-1 rounded-lg" data-testid="campaign-template-select"><SelectValue placeholder="Select template" /></SelectTrigger>
                <SelectContent>{templates.map(t => <SelectItem key={t.template_id} value={t.template_id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button className="rounded-full bg-[#2D2D2D] text-white" onClick={saveCampaign} data-testid="save-campaign-btn">Create Campaign</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={!!previewData} onOpenChange={() => setPreviewData(null)}>
        <DialogContent className="rounded-2xl max-w-md">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Manrope' }}>Campaign Preview</DialogTitle></DialogHeader>
          {previewData && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 bg-[#F5F5F0] rounded-xl p-3">
                <Users className="w-5 h-5 text-[#2D2D2D]" />
                <span className="text-sm font-medium">{previewData.total_recipients} potential recipients</span>
              </div>
              {previewData.sample?.length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs text-[#888] font-semibold">Sample Recipients:</p>
                  {previewData.sample.map(s => <p key={s.user_id} className="text-sm text-[#2D2D2D]">{s.name} ({s.email})</p>)}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
